import { useRef, useState, type FormEvent } from "react";
import { Container } from "@/shared/components/Container";
import { SectionTag } from "@/shared/components/SectionTag";
import { Reveal } from "@/shared/components/Reveal";
import { useInView } from "@/shared/hooks/useInView";
import { submitContactMessage } from "@/features/home/services/contactApi.service";
import { useLocale } from "@/shared/i18n/locale";
import { getHomeContent } from "@/content/home.content";
import styles from "./Contact.module.css";

type SubmitStatus = "idle" | "loading" | "success" | "error";

const INITIAL_FORM = {
  nome: "",
  empresa: "",
  email: "",
  phone: "",
  cargo: "",
  interesse: "workshop",
  desafio: "",
  website: "",
};

export function Contact() {
  const locale = useLocale();
  const content = getHomeContent(locale);

  const { ref: sectionRef, inView: ringsInView } = useInView<HTMLElement>({
    enterEdgeRatio: 0.1,
    once: true,
  });
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState<SubmitStatus>("idle");
  // Time-trap anti-spam: ms desde que o form montou ate o envio.
  const mountedAt = useRef(Date.now());

  function updateField(field: keyof typeof INITIAL_FORM, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (status === "loading") {
      return;
    }

    setStatus("loading");
    try {
      await submitContactMessage({
        name: form.nome,
        company: form.empresa,
        email: form.email,
        phone: form.phone,
        role: form.cargo,
        interest: form.interesse,
        message: form.desafio,
        website: form.website,
      }, Date.now() - mountedAt.current);
      setStatus("success");
      setForm(INITIAL_FORM);
    } catch {
      setStatus("error");
    }
  }

  const isLoading = status === "loading";

  return (
    <section id="contato" ref={sectionRef} className={styles.contact}>
      <img
        className={`${styles.rings} ${ringsInView ? styles.ringsIn : ""}`}
        src="/assets/aneis-concentricos.svg"
        alt=""
        aria-hidden
        loading="eager"
        decoding="async"
      />

      <Container>
        <div className={styles.grid}>
          <div className={styles.copy}>
            <Reveal>
              <SectionTag tone="light">{content.contact.tag}</SectionTag>
            </Reveal>
            <Reveal delay={80}>
              <h2 className={styles.heading}>
                {content.contact.headingPart1}
                <span className={styles.accent}>{content.contact.headingAccent}</span>
                {content.contact.headingPart2}
              </h2>
            </Reveal>
            <Reveal as="p" className={styles.text} delay={140}>
              {content.contact.text}
            </Reveal>
          </div>

          <form id="contato-formulario" data-lead-form className={styles.form} onSubmit={handleSubmit}>
            <Reveal className={styles.field}>
              <input
                className={styles.input}
                type="text"
                name="nome"
                placeholder={content.contact.namePlaceholder}
                aria-label={content.contact.nameAria}
                value={form.nome}
                onChange={(event) => updateField("nome", event.target.value)}
                required
              />
            </Reveal>
            <Reveal className={styles.field} delay={60}>
              <input
                className={styles.input}
                type="text"
                name="empresa"
                placeholder={content.contact.companyPlaceholder}
                aria-label={content.contact.companyAria}
                value={form.empresa}
                onChange={(event) => updateField("empresa", event.target.value)}
                required
              />
            </Reveal>
            <Reveal className={styles.field} delay={120}>
              <input
                className={styles.input}
                type="email"
                name="email"
                placeholder={content.contact.emailPlaceholder}
                aria-label={content.contact.emailAria}
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
                required
              />
            </Reveal>
            <Reveal className={styles.field} delay={180}>
              <input
                className={styles.input}
                type="text"
                name="cargo"
                placeholder={content.contact.rolePlaceholder}
                aria-label={content.contact.roleAria}
                value={form.cargo}
                onChange={(event) => updateField("cargo", event.target.value)}
              />
            </Reveal>
            <Reveal className={`${styles.field} ${styles.fieldFull}`} delay={80}>
              <input
                className={styles.input}
                type="tel"
                name="phone"
                autoComplete="tel"
                maxLength={40}
                placeholder={content.contact.phonePlaceholder}
                aria-label={content.contact.phoneAria}
                value={form.phone}
                onChange={(event) => updateField("phone", event.target.value)}
              />
            </Reveal>
            <Reveal className={`${styles.field} ${styles.fieldFull}`} delay={80}>
              <select
                className={styles.select}
                name="interesse"
                aria-label={content.contact.interestLabel}
                value={form.interesse}
                onChange={(event) => updateField("interesse", event.target.value)}
              >
                <option value="workshop">{content.contact.interestOptions.workshop}</option>
                <option value="consultoria">{content.contact.interestOptions.consultoria}</option>
                <option value="treinamento">{content.contact.interestOptions.treinamento}</option>
                <option value="projeto">{content.contact.interestOptions.projeto}</option>
              </select>
            </Reveal>
            <Reveal className={`${styles.field} ${styles.fieldFull}`} delay={140}>
              <textarea
                className={styles.textarea}
                name="desafio"
                placeholder={content.contact.challengePlaceholder}
                aria-label={content.contact.challengeAria}
                value={form.desafio}
                onChange={(event) => updateField("desafio", event.target.value)}
              />
            </Reveal>

            {/* Honeypot anti-spam: oculto para humanos; bots tendem a preencher. */}
            <div className={styles.honeypot} aria-hidden>
              <label htmlFor="contact-website">{content.contact.honeypotLabel}</label>
              <input
                id="contact-website"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(event) => updateField("website", event.target.value)}
              />
            </div>

            <Reveal className={styles.submitReveal} delay={200}>
              <button type="submit" className={styles.submit} disabled={isLoading}>
                {isLoading ? content.contact.submitLoading : content.contact.submitIdle}
              </button>
            </Reveal>

            {status === "success" && (
              <p className={`${styles.feedback} ${styles.feedbackSuccess}`} role="status">
                {content.contact.successMessage}
              </p>
            )}
            {status === "error" && (
              <p className={`${styles.feedback} ${styles.feedbackError}`} role="alert">
                {content.contact.errorMessage}
              </p>
            )}
          </form>
        </div>
      </Container>
    </section>
  );
}
